import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Eye, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  adminKycQueue, adminKycDocUrl, adminKycApprove, adminKycReject,
} from "@lawyer/services/kycApi";

const STATUS_PILL = {
  pending_review: { cls: "bg-amber-100 text-amber-800", label: "Pending" },
  approved: { cls: "bg-emerald-100 text-emerald-800", label: "Approved" },
  rejected: { cls: "bg-rose-100 text-rose-800", label: "Rejected" },
};

const TABS = [
  { key: "pending_review", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

export default function AdminKycReview() {
  const [status, setStatus] = useState("pending_review");
  const [data, setData] = useState({ total: 0, items: [] });
  const [busy, setBusy] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await adminKycQueue({ status, limit: 50 });
      setData(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load"); }
  }, [status]);
  useEffect(() => { reload(); }, [reload]);

  const view = async (item) => {
    setBusy(`view-${item.id}`);
    try {
      const r = await adminKycDocUrl(item.lawyer_id, item.id);
      // Open in new tab — backend returns absolute URL for S3 or relative for local FS
      const url = r.url?.startsWith("http") ? r.url : `${process.env.REACT_APP_BACKEND_URL}${r.url}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not fetch URL"); }
    finally { setBusy(null); }
  };
  const approve = async (item) => {
    setBusy(`ok-${item.id}`);
    try { await adminKycApprove(item.lawyer_id, item.id); toast.success("Approved"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };
  const reject = async (item) => {
    const reason = window.prompt("Reason for rejection (shown to the lawyer)?");
    if (!reason) return;
    setBusy(`no-${item.id}`);
    try { await adminKycReject(item.lawyer_id, item.id, reason); toast.success("Rejected"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-kyc-page">
      <div>
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className="text-[#D4AF37]" />
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>e-KYC Review</h1>
        </div>
        <p className="mt-1 text-sm text-slate-500">Approve or reject lawyer onboarding documents. Approving all four required docs auto-verifies the lawyer.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="kyc-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            data-testid={`kyc-tab-${t.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
              status === t.key ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "bg-white border-slate-200 text-slate-600 hover:border-[#0A2540]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="kyc-list">
        {data.items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No documents in this bucket.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.items.map((item) => {
              const pill = STATUS_PILL[item.status] || { cls: "bg-slate-100 text-slate-700", label: item.status };
              return (
                <li key={item.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`kyc-row-${item.id}`}>
                  <div className="flex-1 min-w-[260px]">
                    <p className="font-bold text-[#0A2540]">{item.lawyer_name || item.lawyer_email}</p>
                    <p className="text-xs text-slate-500">
                      {item.doc_type.replace("_", " ")} · {item.filename} · {(item.size / 1024).toFixed(1)} KB · {item.uploaded_at?.slice(0, 16)?.replace("T", " ")}
                    </p>
                    {item.review_note && <p className="text-xs text-rose-600 mt-1">Note: {item.review_note}</p>}
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${pill.cls}`}>{pill.label}</span>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => view(item)}
                      disabled={busy === `view-${item.id}`}
                      data-testid={`kyc-view-${item.id}`}
                      className="rounded-lg text-xs h-8">
                      {busy === `view-${item.id}` ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} className="mr-1" />} View
                    </Button>
                    {item.status !== "approved" && (
                      <Button
                        size="sm" onClick={() => approve(item)}
                        disabled={busy === `ok-${item.id}`}
                        data-testid={`kyc-approve-${item.id}`}
                        className="rounded-lg text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white">
                        {busy === `ok-${item.id}` ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} className="mr-1" />} Approve
                      </Button>
                    )}
                    {item.status !== "rejected" && (
                      <Button
                        size="sm" variant="destructive" onClick={() => reject(item)}
                        disabled={busy === `no-${item.id}`}
                        data-testid={`kyc-reject-${item.id}`}
                        className="rounded-lg text-xs h-8">
                        {busy === `no-${item.id}` ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} className="mr-1" />} Reject
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <p className="text-[11px] text-slate-400">{data.total} document{data.total === 1 ? "" : "s"} in this bucket.</p>
    </div>
  );
}
